export const adminSections = [
  "coffee",
  "ingredients",
  "packaging",
  "equipment",
  "products",
  "modifiers",
  "settings",
  "staff",
  "reports",
  "refunds",
];
export function readAdminSection() {
  const section = new URLSearchParams(window.location.search).get("section");
  return section && adminSections.includes(section) ? section : "ingredients";
}
export function coherentProduct(record: any, field: string, value: any) {
  const next = { ...record, [field]: value };
  const key = (
    {
      beanIds: "beanId",
      iceLevels: "ice",
      sweetnessLevels: "sweetness",
    } as Record<string, string>
  )[field];
  if (key && !value.includes(record.defaults?.[key]))
    next.defaults = { ...record.defaults, [key]: value[0] };
  return next;
}
export function affectedProducts(catalog: any, section: string, id: string) {
  return (catalog.products || []).filter(
    (p: any) =>
      !p.archived &&
      (["ingredients", "coffee"].includes(section)
        ? [
            ...(p.ingredients || []),
            ...(p.extras || []).flatMap((e: any) => e.ingredients || []),
          ].some((r: any) => r.ingredientId === id) ||
          (p.beanIds || []).includes(id)
        : section === "packaging"
          ? (p.packaging || []).some((r: any) => r.packagingId === id)
          : section === "equipment"
            ? [
                ...(p.equipmentIds || []),
                ...(p.extras || []).flatMap((e: any) => e.equipmentIds || []),
              ].includes(id) ||
              (id === "ice_machine" &&
                (p.iceLevels || []).some(
                  (level: string) =>
                    level !== "none" && p.iceQuantities?.[level] > 0,
                ) &&
                [
                  ...(p.ingredients || []),
                  ...(p.extras || []).flatMap((e: any) => e.ingredients || []),
                ].some((r: any) => r.role === "ice"))
            : false),
  );
}
export type CatalogChange = {
  group: CatalogChangeGroup;
  name: string;
  before: any;
  after: any;
  collection: string;
  field?: string;
};
export type CatalogChangeGroup = "menu" | "costs" | "settings";
export type CatalogChangeKind = "added" | "updated" | "archived" | "removed";
function stable(value: any): string {
  if (Array.isArray(value))
    return JSON.stringify(value.map((x) => JSON.parse(stable(x))));
  if (value && typeof value === "object")
    return JSON.stringify(
      Object.fromEntries(
        Object.keys(value)
          .sort()
          .filter((k) => value[k] !== undefined)
          .map((k) => [k, JSON.parse(stable(value[k]))]),
      ),
    );
  return JSON.stringify(value ?? null);
}
export function catalogChanges(draft: any, published: any): CatalogChange[] {
  const changes: CatalogChange[] = [];
  for (const collection of [
    "ingredients",
    "packaging",
    "equipment",
    "products",
    "drinkGroups",
  ]) {
    const before = published?.[collection] || [],
      after = draft?.[collection] || [];
    for (const id of new Set([...before, ...after].map((r: any) => r.id))) {
      const old = before.find((r: any) => r.id === id),
        next = after.find((r: any) => r.id === id);
      if (stable(old) !== stable(next))
        changes.push({
          group: ["products", "drinkGroups"].includes(collection) ? "menu" : "costs",
          collection,
          name: next?.name || old?.name || id,
          before: old,
          after: next,
        });
    }
  }
  for (const field of new Set([
    ...Object.keys(published?.settings || {}),
    ...Object.keys(draft?.settings || {}),
  ])) {
    if (
      stable(published?.settings?.[field]) !== stable(draft?.settings?.[field])
    )
      changes.push({
        group: "settings",
        collection: "settings",
        field,
        name: field,
        before: published?.settings?.[field],
        after: draft?.settings?.[field],
      });
  }
  return changes;
}
export function changedFields(change: CatalogChange) {
  if (change.collection === "settings") return [change.field!];
  return [
    ...new Set([
      ...Object.keys(change.before || {}),
      ...Object.keys(change.after || {}),
    ]),
  ].filter(
    (key) =>
      key !== "id" &&
      stable(change.before?.[key]) !== stable(change.after?.[key]),
  );
}
export function changeKind(change: CatalogChange): CatalogChangeKind {
  if (change.before == null && change.after != null) return "added";
  if (change.before != null && change.after == null) return "removed";
  if (change.after?.archived === true && change.before?.archived !== true)
    return "archived";
  return "updated";
}
export function groupedChangeCounts(changes: CatalogChange[]) {
  return {
    menu: changes.filter((change) => change.group === "menu").length,
    costs: changes.filter((change) => change.group === "costs").length,
    settings: changes.filter((change) => change.group === "settings").length,
  } satisfies Record<CatalogChangeGroup, number>;
}
export type AdminEditorTarget = {
  section: string;
  collection?: string;
  record?: any;
};
export function changeEditorTarget(
  change: CatalogChange,
  catalog: any,
): AdminEditorTarget {
  if (change.collection === "settings")
    return { section: "settings", collection: "settings", record: catalog.settings };
  const record = change.after;
  const section =
    change.collection === "drinkGroups"
      ? "products"
      : change.collection === "products"
        ? "products"
        : change.collection;
  return {
    section,
    collection: change.collection,
    ...(record ? { record } : {}),
  };
}
export function validationEditorTarget(
  error: string,
  catalog: any,
): AdminEditorTarget | null {
  const normalized = error.toLocaleLowerCase();
  const records = [
    ["products", catalog.products || []],
    ["ingredients", catalog.ingredients || []],
    ["packaging", catalog.packaging || []],
    ["equipment", catalog.equipment || []],
    ["drinkGroups", catalog.drinkGroups || []],
  ] as const;
  const named = records
    .flatMap(([collection, items]) =>
      items.map((record: any) => ({ collection, record })),
    )
    .filter(({ record }) => record?.name)
    .sort((a, b) => b.record.name.length - a.record.name.length)
    .find(({ record }) =>
      normalized.includes(record.name.toLocaleLowerCase()),
    );
  if (named) {
    const section = named.collection === "drinkGroups" ? "products" : named.collection;
    return { section, collection: named.collection, record: named.record };
  }
  if (
    /(station|stasiun|opening|jam buka|overhead|surcharge|tambahan harga)/i.test(
      error,
    )
  )
    return { section: "settings", collection: "settings", record: catalog.settings };
  if (/(drink group|drink name|hot or iced|temperature|recipe must belong)/i.test(error))
    return { section: "products" };
  return null;
}
export const productGroups = [
  { key: "basics", fields: ["name", "description", "imageUrl", "category"] },
  {
    key: "recipe",
    fields: [
      "ingredients",
      "costingProfile",
      "equipmentIds",
      "packaging",
      "publicSurcharge",
    ],
  },
  {
    key: "choices",
    fields: [
      "filterSelection",
      "beanIds",
      "iceLevels",
      "iceQuantities",
      "sweetnessLevels",
      "extras",
    ],
  },
  { key: "defaults", fields: ["defaults"] },
];

export function changeSelection(change: CatalogChange) {
  return { collection: change.collection, id: change.field || change.after?.id || change.before?.id };
}
export function selectionKey(selection: { collection: string; id: string }) {
  return JSON.stringify([selection.collection, selection.id]);
}

/** Present shared metadata and temperature records as one drink-sized unit. */
export function preparationGroups(changes: CatalogChange[], catalog: any) {
  const groups = new Map<string, { id: string; name: string; drink: boolean; changes: CatalogChange[]; parts: string[] }>();
  const memberships = [...(catalog?.drinkGroups ?? []), ...changes.filter(c => c.collection === 'drinkGroups').map(c => c.after ?? c.before)];
  for (const change of changes) {
    const item = changeSelection(change);
    const group = change.collection === 'drinkGroups' ? change.after ?? change.before : change.collection === 'products' ? memberships.find(g => Object.values(g.variants ?? {}).includes(item.id)) : null;
    const id = group ? `drink:${group.id}` : selectionKey(item);
    const row = groups.get(id) ?? { id, name: group?.name ?? change.name, drink: !!group || change.collection === 'products', changes: [] as CatalogChange[], parts: [] as string[] };
    row.changes.push(change);
    row.parts.push(change.collection === 'drinkGroups' ? 'shared' : group ? Object.entries(group.variants).find(([,value]) => value === item.id)?.[0] ?? 'recipe' : change.collection === 'products' ? 'recipe' : change.name);
    groups.set(id, row);
  }
  return [...groups.values()];
}
